import { useCallback, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'
import { Spinner } from './Spinner'

export type ConfirmModalProps = {
  open: boolean
  title: string
  /** Main message; can include inline emphasis */
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Primary action style */
  variant?: 'danger' | 'primary'
  size?: 'sm' | 'md'
  onClose: () => void
  /** Called when the user confirms; while the promise is pending, actions are blocked */
  onConfirm: () => void | Promise<void>
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  size = 'sm',
  onClose,
  onConfirm,
}: ConfirmModalProps) {
  const [pending, setPending] = useState(false)

  const handleClose = useCallback(() => {
    if (!pending) onClose()
  }, [onClose, pending])

  const handleConfirm = useCallback(async () => {
    setPending(true)
    try {
      await onConfirm()
    } finally {
      setPending(false)
    }
  }, [onConfirm])

  return (
    <Modal
      open={open}
      title={title}
      onClose={pending ? undefined : handleClose}
      closeOnEscape={!pending}
      closeOnOverlayClick={!pending}
      showCloseButton={!pending}
      size={size}
      footer={
        <>
          <Button type="button" variant="secondary" disabled={pending} onClick={handleClose}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === 'danger' ? 'danger' : 'primary'}
            disabled={pending}
            onClick={() => void handleConfirm()}
            className="min-w-[7.5rem]"
          >
            {pending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner className="h-4 w-4 border-[1.5px] border-primary-foreground/35 border-t-primary-foreground" />
                <span>Please wait…</span>
              </span>
            ) : (
              confirmLabel
            )}
          </Button>
        </>
      }
    >
      {description != null ? description : null}
    </Modal>
  )
}
